import { ed25519 } from "@noble/curves/ed25519.js";
import { X25519PublicJwk } from "../crypto/x25519.js";
import { DeviceKeys } from "../crypto/keys.js";

export interface EnrollmentProofPayload {
  account_id: number;
  sync_device_id: string;
  public_key_jwk: X25519PublicJwk;
  exp: number;
  sig: string;
}

export function canonicalProofBytes(payload: Omit<EnrollmentProofPayload, "sig">): Uint8Array {
  const canonical = {
    account_id: payload.account_id,
    exp: payload.exp,
    public_key_jwk: payload.public_key_jwk,
    sync_device_id: payload.sync_device_id,
  };
  return new TextEncoder().encode(JSON.stringify(canonical));
}

export function signEnrollmentProof(
  trustedDevice: DeviceKeys,
  accountId: number,
  newDeviceId: string,
  newDevicePublicJwk: X25519PublicJwk,
  exp: number,
): string {
  const payloadWithoutSig = {
    account_id: accountId,
    sync_device_id: newDeviceId,
    public_key_jwk: newDevicePublicJwk,
    exp,
  };
  const message = canonicalProofBytes(payloadWithoutSig);
  const signature = ed25519.sign(message, trustedDevice.signingPrivateKey);
  const payload: EnrollmentProofPayload = {
    ...payloadWithoutSig,
    sig: Buffer.from(signature).toString("base64url"),
  };
  return Buffer.from(JSON.stringify(payload)).toString("base64");
}

export function verifyEnrollmentProof(
  trustedSigningPublicKey: Uint8Array,
  proofB64: string,
): EnrollmentProofPayload {
  const payload = JSON.parse(
    Buffer.from(proofB64, "base64").toString("utf8"),
  ) as EnrollmentProofPayload;
  const { sig, ...rest } = payload;
  const message = canonicalProofBytes(rest);
  const signature = Buffer.from(sig, "base64url");
  if (!ed25519.verify(signature, message, trustedSigningPublicKey)) {
    throw new Error("invalid_enrollment_proof_signature");
  }
  if (payload.exp <= Math.floor(Date.now() / 1000)) {
    throw new Error("enrollment_proof_expired");
  }
  return payload;
}
